import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getAccountTypesByAgreement, getUserDevicesByAgreement, getUserProgramsByAgreement } from "../../api/Agreement";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import { getUsersByTenant, createUserHardwareFormSubmit } from "../../api/Users";
import LegacyCreateUserForm from "../../components/user/LegacyCreateUserForm";

/**
 * The production create-user page (commit 370850a): posts the form to the support mailbox.
 * Shown for an agreement without SCO forms (PBI #386); the SCO CreateUsersPage serves the rest.
 */
const LegacyCreateUsersPage = () => {
  const { agreementPublicId, tenantPublicId } = useParams();
  const [selected, setSelected] = useState(["New user"]);
  const { getAccessToken } = useAuth();

  const [accountTypes, setAccountTypes] = useState([]);
  const [accountTypesLoaded, setAccountTypesLoaded] = useState(false);
  const [billingGroups, setBillingGroups] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [laptopSelections, setLaptopSelections] = useState("");
  const [monitorSelections, setMonitorSelections] = useState("");
  const [keyboardSelections, setKeyboardSelections] = useState("");
  const [intuneProgramSelections, setIntuneProgramSelections] = useState([]);
  const [users, setUsers] = useState([]);
  const [userDevices, setUserDevices] = useState([]);
  const [userDevicesLoaded, setUserDevicesLoaded] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [userPrograms, setUserPrograms] = useState([]);
  const [userProgramsLoaded, setUserProgramsLoaded] = useState(false);

  
  const [
    manualInstallationProgramSelections,
    setManualInstallationProgramSelections,
  ] = useState([]);
  const [user, setUser] = useState({
    tenantId: tenantPublicId,
    firstName: "",
    lastName: "",
    email: "",
    mobilePhone: "",
    phone: "",
    accountTypeName: "",
    accountTypeId: "",
    office: "",
    department: "",
    comment: "",
    manager: "",
    location: "",
    details: "",
    toWhom: "",
    desiredDelivery: "",
    billingGroupName: "",
    billingGroupPublicId: "",
    laptops: "",
    monitors: "",
    keyboards: "",
    intunePrograms: [],
    manualInstallationProgram: [],
    newUser: "",
    hardware: "",
    useExistingComputer: "",
  });
  
  const navigate = useNavigate();
  const onChange = (updated) => {
    setSelected(updated);
  };

  const [originalUser, setOriginalUser] = useState(user)

    useEffect(() => {
      if (user && originalUser) {
        const isEqual = JSON.stringify(user) === JSON.stringify(originalUser);
        setHasChanges(!isEqual);
      }
    }, [user, originalUser]);

  const userNames = users.map((user) => user.displayName);

  const monitors = ["Monitor small", "Monitor medium", "Monitor big"];
  const keyboards = ["QWERTY", "Not QWERTY", "Other Keyboard"];
  const intunePrograms = ["Microsoft 365", "Adobe Reader"];
  const manualInstallationPrograms = ["WinRAR", "AutoCad", "Solid works"];

  useEffect(() => {
    fetchUsers();
    fetchAccountTypes();
    fetchBillingGroups();
    fetchUserDevices();
    fetchUserPrograms();
  }, [agreementPublicId, tenantPublicId]);

  const fetchUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      const tenantUsers = await getUsersByTenant(tenantPublicId, accessToken);

      setUsers(tenantUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error("Failed to fetch account types:", error);
      setUsersLoaded(true);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const accessToken = await getAccessToken();
      const aT = await getAccountTypesByAgreement(
        agreementPublicId,
        accessToken
      );

      setAccountTypes(aT);
      setAccountTypesLoaded(true);
    } catch (error) {
      console.error("Failed to fetch account types:", error);
      setAccountTypesLoaded(true);
    }
  };

  const fetchUserDevices = async () => {
    try {
      const accessToken = await getAccessToken();
      const uD = await getUserDevicesByAgreement(
        agreementPublicId,
        accessToken
      );

      setUserDevices(uD);
      setUserDevicesLoaded(true);
    } catch (error) {
      console.error("Failed to fetch user devices:", error);
      setUserDevicesLoaded(true);
    }
  };

  const fetchUserPrograms = async () => {
    try {
      const accessToken = await getAccessToken();
      const uP = await getUserProgramsByAgreement(
        agreementPublicId,
        accessToken
      );

      setUserPrograms(uP);
      setUserProgramsLoaded(true);
    } catch (error) {
      console.error("Failed to fetch user programs:", error);
      setUserProgramsLoaded(true);
    }
  };


  const fetchBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByAgreement(
        agreementPublicId,
        accessToken
      );
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBillingGroupChange = (selectedName) => {
    const selectedGroup = billingGroups.find(
      (group) => group.name === selectedName
    );
    if (!selectedGroup) return;

    setUser((prevUser) => ({
      ...prevUser,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  };

  const handleAccountTypeChange = (selectedName) => {
    const selectedType = accountTypes.find(
      (type) => type.name === selectedName
    );
    if (!selectedType) return;

    setUser((prevUser) => ({
      ...prevUser,
      accountTypeName: selectedType.name,
      accountTypePublicId: selectedType.accountTypePublicId,
    }));
    setHasChanges(true);
  };

  const handleManagerChange = (selectedName) => {
    const selectedUser = users.find(
      (user) => user.displayName === selectedName
    );
    if (!selectedUser) return;
  
    setUser((prevUser) => ({
      ...prevUser,
      managerName: selectedUser.displayName,
      managerPublicId: selectedUser.userPublicId,
    }));
    setHasChanges(true);

  };

  const toggleLaptop = (deviceName, reset = false) => {
    if (reset) {
      setLaptopSelections([]); // eller dispatch tom lista
      return;
    }
    setLaptopSelections(deviceName);
  };

  const toggleMonitor = (option, reset = false) => {
    if (reset) {
      setMonitorSelections([]);
      return;
    }
    setMonitorSelections(option)
  };

  const toggleKeyboard = (keys, reset = false) => {
    if (reset) {
      setKeyboardSelections([]); 
      return;
    }
    setKeyboardSelections(keys)
  };

  const toggleIntuneProgram = (option) => {
    const updated = intuneProgramSelections.includes(option)
      ? intuneProgramSelections.filter((item) => item !== option)
      : [...intuneProgramSelections, option];
    setIntuneProgramSelections(updated);
    setHasChanges(true);
  };

  const toggleManualProgram = (option) => {
    const updated = manualInstallationProgramSelections.includes(option)
      ? manualInstallationProgramSelections.filter((item) => item !== option)
      : [...manualInstallationProgramSelections, option];
    setManualInstallationProgramSelections(updated);
    setHasChanges(true);
  };

  const handleCreateUser = async () => {
    const additionalFields = {};
    customFields.forEach(({ key, value }) => {
      if (key) additionalFields[key] = value;
    });

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }
      
      const formData = {
        tenantId: tenantPublicId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        mobilePhone: user.mobilePhone,
        phone: user.phone,
        accountTypePublicId: user.accountTypePublicId,
        office: user.office,
        department: user.department,
        manager: user.managerName,
        billingGroupPublicId: user.billingGroupName,
        location: user.location,
        details: user.details,
        toWhom: user.toWhom,
        desiredDelivery: user.desiredDelivery,
        comment: user.comment,
        userDevices: user.userDevices,
        userPrograms: user.userPrograms,

        // Dessa är från andra states
        newUser: selected.includes("New user"),
        hardware: selected.includes("Hardware"),
        useExistingComputer: selected.includes("Use existing computer"),
        intunePrograms: intuneProgramSelections,
        manualInstallationProgram: manualInstallationProgramSelections,
/*         additionalFields: additionalFields,
 */      };

      // The API resolves the recipient from its configuration by source (PBI #386).
      const payload = { source: "Dip", formData };

      await createUserHardwareFormSubmit(payload, accessToken);

      toast.success("User created successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error("Failed to create user");
    }
    navigate(-1);
  };

  const toggleSelection = (option) => {
    const updated = selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option];
    onChange(updated);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Create User</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleCreateUser}
                >
                  Save user
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <LegacyCreateUserForm
                user={user}
                setUser={setUser}
                setHasChanges={setHasChanges}
                billingGroups={billingGroups}
                accountTypes={accountTypes}
                onBillingGroupChange={handleBillingGroupChange}
                onAccountTypeChange={handleAccountTypeChange}
                accountTypeNames={accountTypes}
                selected={selected}
                toggleSelection={toggleSelection}
                laptopSelections={laptopSelections}
                toggleLaptop={toggleLaptop}
                monitorSelections={monitorSelections}
                toggleMonitor={toggleMonitor}
                keyboardSelections={keyboardSelections}
                toggleKeyboard={toggleKeyboard}
                intuneProgramSelections={intuneProgramSelections}
                toggleIntuneProgram={toggleIntuneProgram}
                manualInstallationProgramSelections={
                  manualInstallationProgramSelections
                }
                toggleManualProgram={toggleManualProgram}
                users={users}
                onManagerChange={handleManagerChange}
                userDevices={userDevices}
                monitors={monitors}
                keyboards={keyboards}
                intunePrograms={intunePrograms}
                manualInstallationPrograms={manualInstallationPrograms}
                userPrograms={userPrograms}
                onUserProgramsChange={setUserPrograms}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default LegacyCreateUsersPage;
